import { orderViewColumns } from '@/pages/OrderManagement/components/Grid/Columns/OrderColumnCellrenderers'
import { BookOutlined, FileDoneOutlined } from '@ant-design/icons'
import { orderStates } from '@components/Reports/constants'
import {
  GraviGrid,
  Horizontal,
  SingleDateCell,
  TagCell,
  Texto,
  Vertical,
  addCommasToNumber,
} from '@gravitate-js/excalibrr'
import { useUserSettings } from '@hooks/settings'
import { formatters } from '@utils/intl/dateTimeFormatters'
import { Select } from 'antd'
import moment from 'moment'
import { useCallback, useEffect, useMemo, useState } from 'react'

export function DirectiveOrders({ ordersLoading, unfilteredOrders, showModal }) {
  const { Option } = Select
  const [filteredOrders, setFilteredOrders] = useState()
  const [selectedOrderState, setSelectedOrderState] = useState(orderStates.all)
  useEffect(() => {
    setSelectedOrderState(orderStates.all)
    setFilteredOrders([...unfilteredOrders])
  }, [unfilteredOrders])

  const handleSelect = (value) => {
    switch (value) {
      case 'All':
        setFilteredOrders(unfilteredOrders)
        setSelectedOrderState(orderStates.all)
        break
      case 'Unassigned':
        {
          const results = unfilteredOrders.filter((order) => order.state === 'accepted')
          setFilteredOrders(results)
          setSelectedOrderState(orderStates.accepted)
        }
        break
      case 'Assigned':
        {
          const results = unfilteredOrders.filter((order) => order.state === 'assigned')
          setFilteredOrders(results)
          setSelectedOrderState(orderStates.assigned)
        }
        break
      case 'In Progress':
        {
          const results = unfilteredOrders.filter((order) => order.state === 'in progress')
          setFilteredOrders(results)
          setSelectedOrderState(orderStates['in progress'])
        }
        break
      case 'Completed':
        {
          const results = unfilteredOrders.filter((order) => order.state === 'complete')
          setFilteredOrders(results)
          setSelectedOrderState(orderStates.complete)
        }
        break
      default:
        setFilteredOrders(unfilteredOrders)
        setSelectedOrderState(orderStates.all)
    }
  }
  const { updateSetting } = useUserSettings()
  const getRowId = useCallback((row) => row?.data?.id, [])
  const agPropOverrides = useMemo(
    () => ({
      getRowId,
      suppressCellSelection: true,
      frameworkComponents: { ...orderViewColumns, volumeCR: VolumeCR, dateTimeCell: SingleDateCell },
      columnDefaultOverrides: {
        resizeable: false,
        flex: undefined,
        filter: false,
        suppressExcelExport: true,
      },
      rowHeight: 60,
    }),
    []
  )
  const columnDefs = useMemo(() => getColumnDefs(showModal), [])

  return (
    <Vertical border='all' borderRadius={3} flex={3} background='bg-2'>
      <Horizontal verticalCenter justifyContent='space-between' className=' px-4 py-3 '>
        <Texto appearance='medium' weight='bold'>
          DIRECTIVE ORDERS
        </Texto>
        <Select
          size='small'
          value={selectedOrderState}
          onChange={(value) => handleSelect(value)}
          style={{ width: '20%' }}
        >
          {Object.keys(orderStates).map((key) => (
            <Option key={key} value={orderStates[key]}>
              {orderStates[key]}
            </Option>
          ))}
        </Select>
      </Horizontal>
      <Vertical flex={1} scroll>
        <GraviGrid
          storageKey='directives'
          loading={ordersLoading}
          agPropOverrides={agPropOverrides}
          rowData={filteredOrders}
          columnDefs={columnDefs}
          onGridConfigChanged={(config) => updateSetting('gridConfig::directives', config)}
        />
      </Vertical>
    </Vertical>
  )
}
const getColumnDefs = (showModal, lockedOrders) => [
  {
    headerName: '',
    field: 'id',
    cellRenderer: 'buttonsCellRenderer',
    filter: false,
    cellRendererParams: { showModal, lockedOrders, isCompact: true },
    sortable: false,
    menuTabs: [],
    minWidth: 130,
  },
  {
    headerName: 'Load Time',
    valueGetter: (params) => (params.data.loads && params.data.loads[0].eta) || params.data.carrier_window_start,
    field: 'loads',
    cellRenderer: (params) =>
      params.value && (
        <Texto weight='bold'>{formatters.TIME(moment(params.value), params.data.carrier_window_start_local)}</Texto>
      ),
    menuTabs: ['filterMenuTab', 'generalMenuTab'],
    flex: 3,
  },
  {
    field: 'status',
    cellRenderer: 'dispatchStatusCellRenderer',
    cellRendererParams: { showTime: false },
    menuTabs: ['filterMenuTab', 'generalMenuTab'],
    valueGetter: (params) => params.data.trip_status || params.data.state,
    flex: 4,
    cellClass: 'stringType',
  },
  {
    headerName: 'Destinations',
    autoHeight: true,
    field: 'drops',
    menuTabs: ['filterMenuTab', 'generalMenuTab'],
    valueGetter: (params) => params.data.drops.map((drop) => `${drop.location_name}, ${drop.store_name}`),
    cellRenderer: 'destinationsCellRenderer',
    cellRendererParams: {
      setOrderHistoryStoreId: null,
    },
    flex: 4.5,
  },
  {
    field: 'volume',
    cellRenderer: 'volumeCR',
    valueGetter: (params) => (params.data.bol_volume > 0 ? params.data.bol_volume : params.data.volume),
    menuTabs: ['filterMenuTab', 'generalMenuTab'],
    flex: 3,
  },
]

function VolumeCR({ value, data }) {
  const hasBOLVolume = data.bol_volume > 0
  return (
    <>
      {hasBOLVolume ? (
        <TagCell
          value={addCommasToNumber(value)}
          className='volume-tag flex justify-center items-center'
          tagTheme={{ theme1: true }}
          icon={<BookOutlined />}
          iconClass='volume-tag-icon pr-2'
          tipTitle={`BOL Volume: ${addCommasToNumber(value)}`}
        />
      ) : (
        <TagCell
          value={addCommasToNumber(value)}
          className='volume-tag flex justify-center items-center '
          tagTheme={{ theme2: true }}
          icon={<FileDoneOutlined />}
          iconClass='volume-tag-icon pr-2'
          tipTitle={`Order Volume: ${addCommasToNumber(value)}`}
        />
      )}
    </>
  )
}
